import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, TextField, Typography, useMediaQuery } from "@mui/material";
import { useParams } from "react-router-dom";
import {
  GetShareAccessTokenResponse,
  Project,
  Share,
} from "../../proto/api_pb";
import {
  getShareAccessToken,
  increaseShareUrlViews,
} from "../../services/shareService";


import { ContainerForGrid } from "../../components/Exolore/styles";
import { SingleProject } from "../../components/SingleProject/SingleProject";
import { styles } from "../Projects/styles";


import { toast } from "react-toastify";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";
import { getProject } from "../../services/projectService";

export const SharedProjectsPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [share, setShare] = useState<Share>();
  const [shareToken, setShareToken] = useState<string>();
  const [, setAllowDownload] = useState<boolean>(false);
  const [isMaxViewsReached, setIsMaxViewsReached] = useState<boolean>(false);
  const [isPasswordRequired, setIsPasswordRequired] = useState<boolean>(false);
  const [passwordInput, setPasswordInput] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [passwordLoading, setPasswordLoading] = useState<boolean>(false);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const { id } = useParams();
  const isMobileScreen = useMediaQuery("(max-width:900px)");
  const [session] = useState<any>(null);
  const [, setInbox] = useState<any>(null);
  const { page, scrollContainerRef, setIsScrollEnabled, setPage, isScrollEnabled } = useInfiniteScroll();
  const isFetchingRef = useRef(false);
  const wasScrollDisabledRef = useRef(true);
  const PAGE_SIZE = 8;

  const handleShareResponse = async (response: any) => {
    if (response instanceof GetShareAccessTokenResponse) {
      const shareData = response.getShare();
      if (shareData) {
        setShare(shareData);
        setShareToken(response.getAccessToken());
        setAllowDownload(shareData.getAllowDownload());

        if (
          !shareData.getUnlimitedViews() &&
          shareData.getImpressionCount() >= shareData.getMaxViews()
        ) {
          setIsMaxViewsReached(true);
          toast.error("This share link has reached its maximum view limit");
          return;
        }

        await increaseShareUrlViews(id!, response.getAccessToken());

        const shareType = shareData.getShareType() as number;
        const objectId = shareData.getObjectId();

        const isProjectsCollection = shareType === 5 || (objectId && objectId.includes(","));

        if (!isProjectsCollection) {
          toast.error("Invalid share type");
          return;
        }

        const ids = shareData.getObjectId().split(",").map(id => id.trim()).filter(Boolean);

        if (ids.length === 0) {
          toast.error("No projects found in share");
          return;
        }

        setProjectIds(ids);
      }
    }
  };

  useEffect(() => {
    const fetchShareData = async () => {
      if (!id) return;

      try {
        const response = await getShareAccessToken(id);
        await handleShareResponse(response);
      } catch (error: any) {
        const errorMessage = error?.message || "";
        if (errorMessage.includes("password required")) {
          setIsPasswordRequired(true);
        } else {
          console.error("Error fetching share data:", error);
          toast.error("Failed to load shared projects");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchShareData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handlePasswordSubmit = async () => {
    if (!id || !passwordInput.trim()) return;
    setPasswordLoading(true);
    setPasswordError("");

    try {
      const response = await getShareAccessToken(id, passwordInput.trim());
      await handleShareResponse(response);
      setIsPasswordRequired(false);
    } catch (error: any) {
      const errorMessage = error?.message || "";
      if (errorMessage.includes("incorrect password")) {
        setPasswordError("Incorrect password");
      } else if (errorMessage.includes("password required")) {
        setPasswordError("Password is required");
      } else {
        setPasswordError("Failed to access shared projects");
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  useEffect(() => {
    const fetchProjects = async () => {
      if (projectIds.length === 0 || !shareToken || !share) return;
      if (isFetchingRef.current) return;

      const startIndex = page * PAGE_SIZE;
      const endIndex = startIndex + PAGE_SIZE;
      const idsToFetch = projectIds.slice(startIndex, endIndex);

      if (idsToFetch.length === 0) {
        setIsScrollEnabled(false);
        return;
      }

      isFetchingRef.current = true;
      try {
        const enterpriseId = share.getEnterpriseId() || "";
        const promises = idsToFetch.map(pid => getProject(shareToken, pid, enterpriseId));
        const responses = await Promise.all(promises);

        const found: Project[] = [];
        for (const res of responses) {
          if (res && 'getProject' in res && typeof res.getProject === 'function') {
            const p = res.getProject();
            if (p) found.push(p);
          }
        }

        setProjects(prev => {
          const existingIds = new Set(prev.map(p => p.getId()));
          const newProjects = found.filter(p => !existingIds.has(p.getId()));
          return [...prev, ...newProjects];
        });

        if (endIndex >= projectIds.length) {
          setIsScrollEnabled(false);
        } else {
          setIsScrollEnabled(true);
          setIsScrollEnabled(true);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
        toast.error("Failed to load projects");
      } finally {
        isFetchingRef.current = false;
      }
    };

    fetchProjects();
  }, [projectIds, page, shareToken, share, setIsScrollEnabled]);

  // After each project batch loads, keep auto-filling if container is still not full.
  // The hook's auto-fill only fires on the first false→true transition; this handles pages 1+.
  useEffect(() => {
    if (!isScrollEnabled) {
      wasScrollDisabledRef.current = true;
      return;
    }
    // Skip the first false→true transition — useInfiniteScroll's auto-fill handles that.
    if (wasScrollDisabledRef.current) {
      wasScrollDisabledRef.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const container = scrollContainerRef.current;
      if (!container || isFetchingRef.current) return;
      if (container.scrollHeight <= container.clientHeight) {
        setPage(prev => prev + 1);
      }
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, isScrollEnabled]);

  return (
    <>
      <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
        <Box ref={scrollContainerRef} sx={styles.rightSideContainer}>
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "40vh",
              }}
            >
              <CircularProgress />
            </Box>
          ) : isMaxViewsReached ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "40vh",
              }}
            >
              <Typography>This share link has reached its maximum view limit.</Typography>
            </Box>
          ) : isPasswordRequired ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "40vh",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  padding: "32px",
                  borderRadius: "8px",
                  border: "1px solid #333",
                  maxWidth: "400px",
                  width: "100%",
                }}
              >
                <Typography sx={{ fontWeight: "bold", fontSize: "20px", textAlign: "center" }}>
                  This share is password protected
                </Typography>
                <TextField
                  type="password"
                  placeholder="Enter password"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handlePasswordSubmit();
                  }}
                  error={!!passwordError}
                  helperText={passwordError}
                  fullWidth
                />
                <Button
                  variant="contained"
                  onClick={handlePasswordSubmit}
                  disabled={passwordLoading || !passwordInput.trim()}
                  sx={{ textTransform: "none" }}
                >
                  {passwordLoading ? <CircularProgress size={24} /> : "Submit"}
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  ...styles.projectsHeaderContainer,
                  width: isMobileScreen ? "85vw" : "100%",
                }}
              >
                <Box sx={styles.headerContainer}>
                  <Typography sx={{ fontWeight: "bold", fontSize: "30px" }}>
                    {share?.getTrackTitle() || "Shared Projects"}
                  </Typography>
                </Box>
              </Box>
              <ContainerForGrid
                sx={
                  isMobileScreen ? { marginBottom: "90px" } : { marginTop: "15px" }
                }
              >
                {projects.map((project, index) => (
                  <SingleProject
                    key={index}
                    index={index}
                    project={project}
                    session={session}
                    setInbox={setInbox}
                    handleUseEffect={async () => { }}
                    openFoldersList={[]}
                    setOpenFoldersList={() => { }}
                    isReorderAvailable={false}
                    handleDragStart={() => { }}
                    handleDrop={() => { }}
                    setDraggedIndex={() => { }}
                    onClick={(project) => {
                      window.location.href = `/share/projects/${id}/project/${encodeURIComponent(project.getName())}/${project.getId()}`;
                    }}
                  />
                ))}
              </ContainerForGrid>
              {isScrollEnabled && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {projects.length === 0 && !isLoading && (
                <Box sx={styles.emptyMessageContainer}>
                  <Typography>No projects found!</Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

